import React, { useEffect, useState } from 'react';
import { CheckCircle2, Download, FileText, RefreshCw, Send, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractsHub({ authFetch }) {
  const [contracts, setContracts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({
    name: '',
    description: '',
    recipientName: '',
    recipientEmail: '',
    testMode: false,
    paymentPackageId: '',
    paymentCtaLabel: 'Pagar ahora',
  });

  const loadContracts = async () => {
    const res = await authFetch(`${API}/admin/contracts`);
    const data = await res.json();
    setContracts(data.contracts || []);
    setTemplates(data.templates || []);
    setPaymentOptions(data.payment_package_options || []);
  };

  const applyTemplate = (template) => {
    setForm((prev) => ({
      ...prev,
      name: template.name,
      description: [
        'ALCANCE DEL SERVICIO:',
        ...(template.service_scope || []).map((item) => `- ${item}`),
        '',
        'OBLIGACIONES DEL CLIENTE:',
        ...(template.client_obligations || []).map((item) => `- ${item}`),
        '',
        'OBLIGACIONES DE NEURALIA369:',
        ...(template.provider_obligations || []).map((item) => `- ${item}`),
        '',
        'NOTAS LEGALES Y DE PROTECCIÓN OPERATIVA:',
        ...(template.legal_notes || []).map((item) => `- ${item}`),
      ].join('\n')
    }));
    setShowCreate(true);
  };

  useEffect(() => {
    loadContracts();
  }, []);

  const createContract = async (e) => {
    e.preventDefault();
    if (!file) { setMessage('Adjunta un PDF del contrato'); return; }
    const body = new FormData();
    body.append('name', form.name);
    body.append('description', form.description);
    body.append('recipients_json', JSON.stringify([{ name: form.recipientName, email: form.recipientEmail }]));
    body.append('test_mode', String(form.testMode));
    body.append('payment_package_id', form.paymentPackageId);
    body.append('payment_cta_label', form.paymentCtaLabel);
    body.append('document_file', file);
    const res = await authFetch(`${API}/admin/contracts`, { method: 'POST', body });
    const data = await res.json();
    if (!res.ok) { setMessage(data.detail || 'No se pudo crear el contrato'); return; }
    setMessage('Contrato creado');
    setShowCreate(false);
    setForm({ name: '', description: '', recipientName: '', recipientEmail: '', testMode: false, paymentPackageId: '', paymentCtaLabel: 'Pagar ahora' });
    setFile(null);
    loadContracts();
  };

  const sendContract = async (contractId) => {
    setBusyId(contractId);
    const res = await authFetch(`${API}/admin/contracts/${contractId}/send`, { method: 'POST' });
    const data = await res.json();
    setMessage(res.ok ? 'Contrato enviado a firma' : (data.detail || 'No se pudo enviar'));
    setBusyId(null);
    if (res.ok) loadContracts();
  };

  const refreshContract = async (contractId) => {
    setBusyId(contractId);
    const res = await authFetch(`${API}/admin/contracts/${contractId}/refresh`, { method: 'POST' });
    const data = await res.json();
    setMessage(res.ok ? 'Estado del contrato actualizado' : (data.detail || 'No se pudo actualizar'));
    setBusyId(null);
    if (res.ok) loadContracts();
  };

  const downloadSigned = async (contractId) => {
    const res = await authFetch(`${API}/admin/contracts/${contractId}/download`);
    if (!res.ok) { const data = await res.json(); setMessage(data.detail || 'No se pudo descargar'); return; }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contrato-${contractId}-firmado.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="space-y-6" data-testid="contracts-hub">
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-[28px] border border-border-subtle bg-card/40 p-5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Firma digital</p>
          <h2 className="mt-2 text-2xl text-text-heading">Contratos con SignWell</h2>
          <p className="mt-2 text-sm text-text-body">Envía contratos desde admin, sigue el estado y descarga el PDF firmado cuando esté listo.</p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate((prev) => !prev)} data-testid="contracts-create-toggle">
          <Plus className="w-4 h-4" /> Nuevo contrato
        </button>
      </div>

      {message && <div className="rounded-2xl border border-border-subtle bg-card/40 px-4 py-3 text-sm text-text-body" data-testid="contracts-message">{message}</div>}

      {templates.length > 0 && (
        <div className="rounded-[28px] border border-border-subtle bg-card/40 p-5" data-testid="contracts-templates-library">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Plantillas preparadas</p>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {templates.map((template) => (
              <div key={template.key} className="rounded-[22px] border border-border-subtle bg-background/55 p-4">
                <p className="text-text-heading font-medium">{template.name}</p>
                <p className="mt-2 text-xs text-text-muted">Incluye alcance, obligaciones por ambas partes y notas para reducir riesgo legal/operativo.</p>
                <button className="btn-secondary mt-4" type="button" onClick={() => applyTemplate(template)} data-testid={`contracts-template-${template.key}`}>
                  Usar plantilla
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-[28px] border border-border-subtle bg-card/40 p-5" data-testid="contracts-legal-note-card">
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Cobertura preparada</p>
        <p className="mt-3 text-sm text-text-body">Las plantillas preparadas incluyen alcance del servicio, obligaciones por ambas partes y notas legales/operativas para reducir riesgo de malentendidos. Mañana podemos conectar esto con firma real y completar Términos, Privacidad y Cookies finales.</p>
        <div className="mt-4 rounded-[20px] border border-border-subtle bg-background/55 p-4">
          <p className="text-sm font-semibold text-text-heading">Base de compliance ya contemplada</p>
          <ul className="mt-3 space-y-2 text-sm text-text-body">
            <li>• Transparencia sobre uso de IA cuando sea relevante.</li>
            <li>• Supervisión humana y límites razonables del sistema.</li>
            <li>• Referencia operativa a RGPD, LOPDGDD y marco AI Act UE.</li>
            <li>• Reparto de responsabilidades sobre datos y accesos de terceros.</li>
          </ul>
        </div>
        <div className="mt-4 rounded-[20px] border border-amber-400/20 bg-amber-500/10 p-4" data-testid="contracts-ai-act-note-card">
          <p className="text-sm font-semibold text-text-heading">Recomendación legal operativa</p>
          <p className="mt-2 text-sm text-text-body">Antes de enviar cualquier contrato en producción, conviene revisar que el documento incluya: transparencia sobre uso de IA, límites del servicio, revisión humana en decisiones sensibles, tratamiento de datos y reparto de responsabilidad entre Neuralia369, cliente y terceros integrados.</p>
        </div>
        <div className="mt-4 rounded-[20px] border border-primary/20 bg-primary/10 p-4" data-testid="contracts-route-note-card">
          <p className="text-sm font-semibold text-text-heading">Ruta recomendada ahora mismo</p>
          <p className="mt-2 text-sm text-text-body">Para salir rápido y con menos riesgo, mantenemos SignWell como firma lista para producción. La firma OTP nativa con hash, IP, documento y canal verificado encaja muy bien como fase 2, una vez quede validado el texto legal por abogado y decidamos el nivel de evidencia requerido por país.</p>
        </div>
      </div>

      {showCreate && (
        <form onSubmit={createContract} className="grid gap-4 rounded-[28px] border border-border-subtle bg-card/40 p-5 md:grid-cols-2" data-testid="contracts-create-form">
          <input className="input-kinesthetic" placeholder="Nombre del contrato" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contracts-name-input" />
          <input className="input-kinesthetic" placeholder="Email del firmante" value={form.recipientEmail} onChange={(e) => setForm({ ...form, recipientEmail: e.target.value })} data-testid="contracts-email-input" />
          <input className="input-kinesthetic" placeholder="Nombre del firmante" value={form.recipientName} onChange={(e) => setForm({ ...form, recipientName: e.target.value })} data-testid="contracts-recipient-input" />
          <input type="file" accept="application/pdf" className="input-kinesthetic" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="contracts-file-input" />
          <textarea className="input-kinesthetic md:col-span-2 min-h-[120px]" placeholder="Descripción interna" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="contracts-description-input" />
          <label className="flex items-center gap-2 text-sm text-text-body">
            <input type="checkbox" checked={form.testMode} onChange={(e) => setForm({ ...form, testMode: e.target.checked })} data-testid="contracts-testmode-input" />
            Enviar en modo test
          </label>
          <select className="input-kinesthetic md:col-span-2" value={form.paymentPackageId} onChange={(e) => setForm({ ...form, paymentPackageId: e.target.value })} data-testid="contracts-payment-package-select">
            <option value="">Sin botón de pago post-firma</option>
            {paymentOptions.map((item) => (
              <option key={item.id} value={item.id}>{item.name} · ${item.price} {String(item.currency || 'usd').toUpperCase()} {!item.public_visible ? '· interno' : ''}</option>
            ))}
          </select>
          <input className="input-kinesthetic md:col-span-2" placeholder="Texto del botón post-firma" value={form.paymentCtaLabel} onChange={(e) => setForm({ ...form, paymentCtaLabel: e.target.value })} data-testid="contracts-payment-cta-input" />
          <div className="md:col-span-2 flex justify-end">
            <button className="btn-primary" type="submit" data-testid="contracts-submit-button"><Send className="w-4 h-4" /> Crear contrato</button>
          </div>
        </form>
      )}

      <div className="grid gap-4">
        {contracts.map((contract) => (
          <div key={contract.id} className="rounded-[24px] border border-border-subtle bg-card/40 p-5" data-testid={`contract-card-${contract.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-text-heading">{contract.name}</h3>
                <p className="mt-1 text-sm text-text-body">{contract.description || 'Sin descripción interna'}</p>
                <p className="mt-2 text-xs uppercase tracking-[0.16em] text-text-muted">Estado: {contract.status}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {contract.status === 'draft' && (
                  <button className="btn-primary" onClick={() => sendContract(contract.id)} disabled={busyId === contract.id} data-testid={`contract-send-${contract.id}`}>
                    <Send className="w-4 h-4" /> Enviar
                  </button>
                )}
                <button className="btn-secondary" onClick={() => refreshContract(contract.id)} disabled={busyId === contract.id} data-testid={`contract-refresh-${contract.id}`}>
                  <RefreshCw className="w-4 h-4" /> Actualizar
                </button>
                {contract.signed_pdf_available && (
                  <button className="btn-secondary" onClick={() => downloadSigned(contract.id)} data-testid={`contract-download-${contract.id}`}>
                    <Download className="w-4 h-4" /> PDF firmado
                  </button>
                )}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-3 text-sm text-text-body">
              <span className="inline-flex items-center gap-2 rounded-full border border-border-subtle px-3 py-2"><FileText className="w-4 h-4 text-primary" /> {contract.file_name}</span>
              {contract.test_mode && <span className="inline-flex items-center gap-2 rounded-full border border-border-subtle px-3 py-2">Modo test</span>}
              {contract.payment_package_id && <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 px-3 py-2" data-testid={`contract-payment-link-${contract.id}`}>Post-firma → {contract.payment_cta_label || 'Pagar ahora'}</span>}
              {contract.payment_followup_email_sent_at && <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 px-3 py-2 text-emerald-300">Email de pago enviado</span>}
              {contract.signed_pdf_available && <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 px-3 py-2 text-emerald-300"><CheckCircle2 className="w-4 h-4" /> PDF guardado</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}